import { Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '@mui/material';
import { WAKEUP_FORM_DATA } from 'common/common.constants';
import { WakeUp } from 'common/interface/props';
import Button from 'components/UI/atoms/Button';
import Grid from 'components/UI/atoms/Grid';
import Line from 'components/UI/atoms/Line';
import Text from 'components/UI/atoms/Text';
import Tooltip from 'components/UI/atoms/Tooltip';
import React from 'react';
import { StyledExcelButton } from './DashBoard';
import { RiFileExcel2Line } from 'react-icons/ri';

type TaskTab = 'ALL' | 'NORMAL' | 'SCHEDULER_CREATE' | 'SCHEDULER_UPDATE';

/**
 * 통계 테이블 컴포넌트
 */
export default function StatisticsTable({
  type,
  excelDownloadEvent,
  excelDownloadEventFirstSeller,
  columns,
  rows,
  interSolType,
  selectSolType,
  changeSolType,
  rawData = [],
  schedulerCreateData = [],
  schedulerUpdateData = [],
}: any) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // ST11 작업 유형 탭: ALL / NORMAL / SCHEDULER_CREATE / SCHEDULER_UPDATE
  const hasSchedulerData = schedulerCreateData.length > 0 || schedulerUpdateData.length > 0;
  const [taskTab, setTaskTab] = React.useState<TaskTab>('NORMAL');

  // 날짜별 3가지 합산 데이터 (전체 탭용)
  const mergedData = React.useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return [];
    }
    return rawData.map((normalRow: any, index: number) => {
      const schedCreate = schedulerCreateData[index];
      const schedUpdate = schedulerUpdateData[index];
      const normalProd = normalRow.migrationProd ?? 0;
      const normalProdAll = normalRow.migrationProdAll ?? 0;
      const normalProdFail = normalRow.migrationProdFail ?? normalProdAll - normalProd;
      const totalProd = normalProd + (schedCreate?.migrationProd ?? 0) + (schedUpdate?.migrationProd ?? 0);
      const totalProdFail =
        normalProdFail + (schedCreate?.migrationProdFail ?? 0) + (schedUpdate?.migrationProdFail ?? 0);
      const totalProdAll = normalProdAll + (schedCreate?.migrationProdAll ?? 0) + (schedUpdate?.migrationProdAll ?? 0);
      const successRate = totalProdAll > 0 ? (totalProd / totalProdAll) * 100 : 0;
      return {
        day: type === 'DAY' ? normalRow.day : `${normalRow.startDate} ~ ${normalRow.endDate}`,
        totalProd,
        totalProdFail,
        totalProdAll,
        successRate,
      };
    });
  }, [rawData, schedulerCreateData, schedulerUpdateData]);

  // 탭 변경 시 페이지 초기화
  const handleTaskTabChange = (tab: TaskTab) => {
    setTaskTab(tab);
    setPage(0);
  };

  const [selectShop, setSelectShop] = React.useState<WakeUp>();

  React.useEffect(() => {
    switch (type) {
      case 'MONTH':
        setSelectShop(selectSolType.selectMonthSolType);
        break;

      case 'WEEK':
        setSelectShop(selectSolType.selectWeekSolType);
        break;

      case 'DAY':
        setSelectShop(selectSolType.selectDaySolType);
        break;
    }
  }, [type, selectSolType]);

  // interSolType 변경 시 탭 초기화 (스케줄러 데이터 있으면 전체 탭으로)
  React.useEffect(() => {
    setTaskTab(hasSchedulerData ? 'ALL' : 'NORMAL');
    setPage(0);
  }, [interSolType, selectSolType, hasSchedulerData]);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 현재 탭에 맞는 데이터 / 컬럼 헤더 결정
  const isAllTab = taskTab === 'ALL';
  const isSchedulerTab = taskTab === 'SCHEDULER_CREATE' || taskTab === 'SCHEDULER_UPDATE';
  const activeData: any[] =
    taskTab === 'ALL'
      ? mergedData
      : taskTab === 'NORMAL'
        ? rows
        : taskTab === 'SCHEDULER_CREATE'
          ? schedulerCreateData
          : schedulerUpdateData;
  const schedulerHeaders =
    taskTab === 'SCHEDULER_CREATE'
      ? ['날짜', '이전 성공 상품 수', '이전 실패 상품 수', '전체 상품 수', '성공 비율']
      : ['날짜', '업데이트 성공 상품 수', '업데이트 실패 상품 수', '전체 상품 수', '성공 비율'];

  const pagedData =
    rowsPerPage > 0 ? activeData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : activeData;
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, activeData.length - page * rowsPerPage);

  return (
    <>
      <Grid display="flex" justifyContent="space-between" alignItems="center">
        <Grid display="flex" bottom={interSolType === 'ALL' ? 10 : 0}>
          <Text fontSize="30px" fontWeight={600} color="#444">
            {type === 'MONTH' ? '월간' : type === 'WEEK' ? '주간' : type === 'DAY' ? '일간' : ''} 통계
          </Text>
        </Grid>
        {!['ALL', 'ESM'].includes(interSolType) && !isSchedulerTab && !isAllTab && (
          <Button
            type="button"
            color="main"
            variant="outlined"
            size="small"
            width={120}
            onClick={() => excelDownloadEvent(type)}
          >
            엑셀 다운로드
          </Button>
        )}
      </Grid>

      <Grid display={'flex'} justifyContent={'space-between'} alignItems="center" top={0} bottom={10}>
        {interSolType === 'ALL' ? (
          <>
            <Grid display={'flex'} width={600} top={0}>
              {['ALL', 'COUPANG', 'ST11', 'ALWAYZ', 'KAKAO'].map((one) => (
                <Grid right={5}>
                  <Button
                    borderRadius={0}
                    type="button"
                    color="main"
                    variant={`${selectShop === one ? 'contained' : 'disabled'}`}
                    size="small"
                    width={120}
                    onClick={() => changeSolType(type, one)}
                  >
                    {WAKEUP_FORM_DATA[one as WakeUp].name.type1}
                  </Button>
                </Grid>
              ))}
            </Grid>
            <Button
              type="button"
              color="main"
              variant="outlined"
              size="small"
              width={120}
              onClick={() => excelDownloadEvent(type)}
            >
              엑셀 다운로드
            </Button>
          </>
        ) : interSolType === 'ESM' ? (
          <>
            <Grid display={'flex'} width={600} top={0}>
              {['ESM_ALL', 'ESM', 'AUCTION', 'GMARKET'].map((one) => (
                <Grid right={5}>
                  <Button
                    borderRadius={0}
                    type="button"
                    color="main"
                    variant={`${selectShop === one ? 'contained' : 'disabled'}`}
                    size="small"
                    width={120}
                    onClick={() => changeSolType(type, one)}
                  >
                    {one === 'ESM_ALL'
                      ? '전체'
                      : one === 'ESM'
                        ? '옥션&G마켓'
                        : WAKEUP_FORM_DATA[one as WakeUp].name.type1}
                  </Button>
                </Grid>
              ))}
            </Grid>
            <Button
              type="button"
              color="main"
              variant="outlined"
              size="small"
              width={120}
              onClick={() => excelDownloadEvent(type)}
            >
              엑셀 다운로드
            </Button>
          </>
        ) : (
          <></>
        )}
      </Grid>

      {/* ST11 작업 유형 탭 (스케줄러 데이터가 있을 때만) */}
      {hasSchedulerData && (
        <Grid display="flex" justifyContent="flex-start" width={500} bottom={12} top={4}>
          {(['ALL', 'NORMAL', 'SCHEDULER_CREATE', 'SCHEDULER_UPDATE'] as TaskTab[]).map((tab) => {
            const label =
              tab === 'ALL'
                ? '전체'
                : tab === 'NORMAL'
                  ? '일반 이전'
                  : tab === 'SCHEDULER_CREATE'
                    ? '자동등록'
                    : '상품업데이트';
            return (
              <Grid right={6} key={tab}>
                <Button
                  borderRadius={4}
                  type="button"
                  color="main"
                  variant={taskTab === tab ? 'contained' : 'disabled'}
                  size="small"
                  width={110}
                  onClick={() => handleTaskTabChange(tab)}
                >
                  {label}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Line height={2} color="#000" />
      <Table>
        <TableHead>
          <TableRow>
            {isAllTab
              ? ['날짜', '이전 성공 상품 수', '이전 실패 상품 수', '전체 상품 수', '성공 비율'].map((h) => (
                  <TableCell key={h} style={{ fontWeight: 'bold' }} align="center" size="small">
                    {h}
                  </TableCell>
                ))
              : isSchedulerTab
                ? schedulerHeaders.map((h) => (
                    <TableCell key={h} style={{ fontWeight: 'bold' }} align="center" size="small">
                      {h}
                    </TableCell>
                  ))
                : Object.entries(columns).map(([key, value]: any) => (
                    <TableCell style={{ fontWeight: 'bold' }} align="center" size="small">
                      {key}
                      {key !== '날짜' ? (
                        <Tooltip title={value}>
                          <Grid color="#f2f4f7" left={2} right={2}>
                            ?
                          </Grid>
                        </Tooltip>
                      ) : (
                        ''
                      )}
                    </TableCell>
                  ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isAllTab
            ? pagedData.map((one: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell align="center">{one.day}</TableCell>
                  <TableCell align="center">{one.totalProd?.toLocaleString() ?? '-'}</TableCell>
                  <TableCell align="center">{one.totalProdFail?.toLocaleString() ?? '-'}</TableCell>
                  <TableCell align="center">{one.totalProdAll?.toLocaleString() ?? '-'}</TableCell>
                  <TableCell align="center">{one.successRate?.toFixed(2) ?? '-'}%</TableCell>
                </TableRow>
              ))
            : isSchedulerTab
              ? pagedData.map((one: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell align="center">
                      {type === 'DAY' ? one.day : `${one.startDate} ~ ${one.endDate}`}
                    </TableCell>
                    <TableCell align="center">{one.migrationProd?.toLocaleString() ?? '-'}</TableCell>
                    <TableCell align="center">{one.migrationProdFail?.toLocaleString() ?? '-'}</TableCell>
                    <TableCell align="center">{one.migrationProdAll?.toLocaleString() ?? '-'}</TableCell>
                    <TableCell align="center">{one.successPercent?.toFixed(2) ?? '-'}%</TableCell>
                  </TableRow>
                ))
              : pagedData.map((one: any) => (
                  <TableRow key={one.index}>
                    <TableCell align="center">{one.day}</TableCell>
                    <TableCell align="center">{one.joinSellerCount}</TableCell>
                    <TableCell align="center">{one.prodCount}</TableCell>
                    <TableCell align="center">
                      {type === 'WEEK' ? (
                        <Grid display="flex" justifyContent="center">
                          {one.migrationFirstSellerCount}
                          <StyledExcelButton
                            color="white"
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              excelDownloadEventFirstSeller(type, one.day, one.migrationFirstSellerCount);
                            }}
                          >
                            <RiFileExcel2Line size="1.2rem" />
                          </StyledExcelButton>
                        </Grid>
                      ) : (
                        one.migrationFirstSellerCount
                      )}
                    </TableCell>
                    <TableCell align="center">{one.migrationSellerCount}</TableCell>
                    <TableCell align="center">{one.sellerAverageProdCount}</TableCell>
                    <TableCell align="center">{one.successRate}</TableCell>
                  </TableRow>
                ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={isAllTab || isSchedulerTab ? 5 : 6} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={activeData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
